<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import NProgress from 'nprogress';
import DeviceMap from '@/components/device/map/DeviceMap.vue';
import MapControlPanel from '@/components/_common/navbar/MapControlPanel.vue';
import DeviceDetailsBar from '@/components/device/details/DeviceDetailsBar.vue';
import DeviceDetailsView from '@/views/DeviceDetailsView.vue';
import DeviceDetailsNavBar from '@/components/device/details/DeviceDetailsNavBar.vue';
import ThreeColumnLayout from "@/components/_common/ThreeColumnLayout.vue";
import { getAccountDevice } from '@gpx-spa/queries';
import { useDevice } from '@/composables/useDevice';
import { usePosition } from '@/composables/usePosition';

import { useSynchronizedSidebars } from "@/composables/useSynchronizedSidebars";
useSynchronizedSidebars({ defaultOpenSidebar: 'left' });

import {useResetChatOnSidebarClose} from "@/composables/useResetChatOnSidebarClose";
useResetChatOnSidebarClose();

import {useResetChatOnAccountOrUserChange} from "@/composables/useResetChatOnAccountOrUserChange";
useResetChatOnAccountOrUserChange();

// Get accountId and deviceId from route
const store = useStore();
const route = useRoute();
const accountId = computed(() => store.getters['auth/accountId']);
const deviceId = computed(() => +route.params.id);

// Query parameters for device details
const queryParams = computed(() => ({
    with_current_position: true,
}));

// Fetch device using vue-query
const { data: deviceData, isLoading: isDeviceLoading } = getAccountDevice(
    accountId,
    deviceId,
    queryParams
);

// Update Pinia store when device data changes
const deviceStore = useDevice();
const positionStore = usePosition();

watch(
    deviceData,
    (newDevice) => {
        if (newDevice) {
            // Create a copy of the device to avoid mutating readonly object from vue-query
            const deviceCopy = { ...newDevice };
            
            if (newDevice.current_position) {
                const position = { ...newDevice.current_position };
                position.accuracy = (position.accuracy / 1000) || 1;
                positionStore.addPosition(position);
                const stored = positionStore.getPositionByDeviceId(newDevice.id);
                if (stored) {
                    deviceCopy.current_position = stored;
                }
            }
            
            // Update deviceView in Pinia store with the copy
            deviceStore.deviceView = deviceCopy;
            
            // Add device to devices map
            deviceStore.addDevice(deviceCopy);
        }
    },
    { immediate: true }
);

// Update loading state in Pinia store and show/hide NProgress
watch(isDeviceLoading, (isLoading) => {
    deviceStore.isLoading = isLoading;
    
    if (isLoading) {
        NProgress.start();
    } else {
        NProgress.done();
    }
}, { immediate: true });

// No need to provide deviceId - DeviceDetailsView can get it from route directly
</script>


<template>
  <ThreeColumnLayout>
    <template #leftTop>
      <DeviceDetailsNavBar />
    </template>
    <template #left>
      <DeviceDetailsView />
    </template>
    <template #rightTop>
      <MapControlPanel>
        <template #filters>
          <DeviceDetailsBar />
        </template>
      </MapControlPanel>
    </template>
    <template #right>
      <DeviceMap :view="`single`" :viewType="`deviceView`" />
    </template>
  </ThreeColumnLayout>
</template>
